import type { Socket } from 'socket.io-client';
import { useAuthStore } from '../stores/authStore';
import { useGameStore, type GamePersistence } from '../stores/gameStore';

// These callbacks belong to the authenticated socket session, even when a
// persistence receipt arrives before game:finished or after leaving the game.
export function bindGameResultEvents(socket: Pick<Socket, 'on' | 'off'>, onIdentityChange?: () => void) {
  const owner = useAuthStore.getState();
  const userId = Number.isSafeInteger(owner.user?.id) && owner.user!.id > 0 ? owner.user!.id : null;
  let active = true;
  const sameSession = () => {
    const current = useAuthStore.getState();
    return active && current.isAuthenticated === owner.isAuthenticated && current.token === owner.token && current.user?.id === owner.user?.id;
  };
  const refreshAccount = (gameId: number, previousStatus?: string) => {
    if (previousStatus === 'saved' || useGameStore.getState().resultPersistence?.status !== 'saved' ||
        !owner.isAuthenticated || !owner.token || userId === null) return;
    void (async () => {
      try {
        const response = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${owner.token}` }, cache: 'no-store' });
        const data = response.ok ? await response.json() : null;
        if (sameSession() && useGameStore.getState().gameId === gameId && data?.success && data.data?.id === userId) {
          useAuthStore.getState().setUser(data.data);
        }
      } catch { /* Commit remains confirmed; the profile fetches its own current data. */ }
    })();
  };
  const starting = (data: { gameId: number; countdown?: number }) => {
    if (!sameSession() || !Number.isSafeInteger(data?.gameId) || data.gameId <= 0) return;
    const store = useGameStore.getState();
    if (data.countdown) store.setCountdown(data.countdown);
    store.startGame(data.gameId, userId);
  };
  const finished = (data: { gameId: number; ranking: any[]; persistence?: GamePersistence }) => {
    if (!sameSession() || data?.gameId !== useGameStore.getState().gameId || !Array.isArray(data.ranking)) return;
    const previousStatus = useGameStore.getState().resultPersistence?.status;
    useGameStore.getState().finishGame(data.ranking, data.gameId, data.persistence);
    refreshAccount(data.gameId, previousStatus);
  };
  const persistence = (data: { gameId: number; persistence: GamePersistence }) => {
    if (!sameSession() || data?.gameId !== useGameStore.getState().gameId) return;
    const previousStatus = useGameStore.getState().resultPersistence?.status;
    useGameStore.getState().updatePersistence(data.gameId, data.persistence);
    refreshAccount(data.gameId, previousStatus);
  };

  socket.on('room:starting', starting);
  socket.on('game:start', starting);
  socket.on('game:finished', finished);
  socket.on('game:persistence', persistence);
  const unsubscribe = useAuthStore.subscribe(() => {
    if (sameSession()) return;
    unbind();
    useGameStore.getState().reset();
    onIdentityChange?.();
  });
  function unbind() {
    if (!active) return;
    active = false;
    unsubscribe();
    socket.off('room:starting', starting);
    socket.off('game:start', starting);
    socket.off('game:finished', finished);
    socket.off('game:persistence', persistence);
  }
  return unbind;
}
